import * as React from 'react';

import { ErrorState } from './error-state';
import { Button } from '../primitives/button';

export interface ErrorBoundaryRenderArgs {
  error: Error;
  /** Clear the error state and re-render children (in-place recovery). */
  reset: () => void;
}

export interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Short label identifying which subtree this boundary guards. */
  scope?: string;
  /**
   * Reported once per caught error. Hosts wire this to their telemetry sink;
   * the boundary itself stays dependency-free.
   */
  onError?: (error: Error, info: React.ErrorInfo, scope?: string) => void;
  /**
   * Custom fallback. When omitted a recoverable default fallback with a
   * "Try again" (reset) and an optional secondary action is rendered, so every
   * boundary in the app shares one consistent, themed recovery surface.
   */
  fallback?: (args: ErrorBoundaryRenderArgs) => React.ReactNode;
  /** Title for the default fallback. */
  title?: string;
  /**
   * Optional secondary action button rendered alongside "Try again" in the
   * default fallback (e.g. "Reload" or "Browse modules"). The boundary owns the
   * primary reset; the host supplies the escalation path.
   */
  secondaryAction?: { label: string; onClick: () => void };
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Single parameterized error boundary for the whole app. Previously the shell
 * (ShellErrorBoundary) and the federated module host (ModuleView) each shipped
 * their own boundary class with divergent fallback markup and recovery logic;
 * this consolidates the catch/report/reset mechanics here so every guard has
 * consistent fallback styling, while callers keep their bespoke recovery via
 * the `fallback` render prop and `secondaryAction`.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo): void {
    this.props.onError?.(error, info, this.props.scope);
  }

  private reset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  override render(): React.ReactNode {
    if (!this.state.hasError || !this.state.error) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback({ error: this.state.error, reset: this.reset });
    }

    return (
      <div
        role="alert"
        className="flex min-h-[60vh] items-center justify-center bg-surface-base p-6"
      >
        <div className="max-w-md text-center">
          <h1 className="mb-2 text-2xl font-semibold tracking-tight text-foreground">
            {this.props.title ?? 'Something went wrong'}
          </h1>
          <p className="mb-6 text-sm text-muted-foreground">
            {this.state.error.message || 'An unexpected error occurred.'}
          </p>
          <div className="flex items-center justify-center gap-3">
            <Button onClick={this.reset} size="sm">
              Try again
            </Button>
            {this.props.secondaryAction && (
              <Button variant="outline" size="sm" onClick={this.props.secondaryAction.onClick}>
                {this.props.secondaryAction.label}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }
}

/**
 * Compact inline fallback for content-area boundaries that shouldn't take the
 * full-height treatment — composes the shared ErrorState banner with a reset.
 */
export function InlineErrorFallback({ error, reset }: ErrorBoundaryRenderArgs): React.ReactElement {
  return <ErrorState title="Couldn’t render this view" message={error} onRetry={reset} />;
}

export default ErrorBoundary;
